import { AutoComplete, Spin } from 'antd'
import type { BaseSelectRef } from 'rc-select'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useParams } from 'react-router'
import { useDebounce } from 'react-use'

import type { SizeType } from 'antd/es/config-provider/SizeContext'
import { ErrorLabel } from 'common/components/ErrorLabel/ErrorLabel'
import { Label } from 'common/components/Label/Label'
import type { EmrCodeItem } from 'features/Procedure/state/procedure-api.type'
import { useLazyFetchEmrCodesQuery } from 'features/Procedure/state/procedure.api'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'

import styles from './upc-select.module.scss'

// Time between keystrokes / search events that qualifies as "scanner speed"
const SCANNER_KEYSTROKE_THRESHOLD_MS = 50
// Minimum chars accumulated at scanner speed before Enter is treated as a scan
const MIN_SCANNER_CHAR_COUNT = 6
// Pause between search events long enough to conclude the user is typing manually
const MANUAL_TYPING_RESET_MS = 500

type Props = {
  value: string
  onChange: (upc: string) => void
  onSelectProduct: (item: EmrCodeItem) => void
  placeholder?: string
  debounceTimeout?: number
  minSearchLength?: number
  disabled?: boolean
  className?: string
  error?: string
  label?: string
  required?: boolean
  size?: SizeType
  autoFocus?: boolean
  onScan?: (upc: string) => void
}

type UpcOption = {
  value: string
  label: React.ReactNode
  item: EmrCodeItem
}

export const UpcSelect = ({
  value,
  onChange,
  onSelectProduct,
  placeholder = 'UPC',
  debounceTimeout = 500,
  minSearchLength = 1,
  disabled = false,
  className,
  error,
  label = 'UPC',
  required = false,
  size = 'large',
  autoFocus = false,
  onScan,
}: Props) => {
  const inputRef = useRef<BaseSelectRef>(null)

  // Shared flag: skip debounced search on the handleSearch call triggered by a scan/paste
  const isScanInputRef = useRef(false)

  // ── Desktop scanner detection (keydown events) ──────────────────────────────
  const lastKeyTimeRef = useRef(0)
  const scannerCharCountRef = useRef(0)
  // Accumulates only the chars typed at scanner speed; independent of DOM value
  const scanBufferRef = useRef('')

  // ── Android scanner detection (input/search events) ─────────────────────────
  // On Android, keydown fires e.key === 'Unidentified' for printable chars, so
  // keydown-based counting is unreliable. We fall back to measuring time between
  // consecutive handleSearch calls instead.
  const lastSearchTimeRef = useRef(0)
  const rapidSearchCountRef = useRef(0)
  // Latest value seen in handleSearch (stripped of any previous-scan prefix)
  const lastSearchValueRef = useRef('')
  // Value of the last completed scan — used to strip a prefix when the scanner
  // fires twice quickly and appends the new barcode to the existing field value
  const lastScannedValueRef = useRef('')

  const [searchValue, setSearchValue] = useState('')
  const [suppressDropdown, setSuppressDropdown] = useState(false)
  const { siteId } = useParams<{ siteId: string }>()

  useEffect(() => {
    if (!autoFocus) return
    const timer = setTimeout(() => inputRef.current?.focus(), 100)
    return () => clearTimeout(timer)
  }, [])

  const [fetchEmrCodes, { isFetching, data }] = useLazyFetchEmrCodesQuery()

  useDebounce(
    () => {
      if (!searchValue || !siteId || searchValue.length < minSearchLength) {
        return
      }

      fetchEmrCodes({
        siteId,
        search: searchValue,
        code_type: ProcedureTypeCode.CPT4,
        category: 'products',
      })
    },
    debounceTimeout,
    [searchValue],
  )

  const options = useMemo<UpcOption[]>(() => {
    if (!data) return []

    return data
      .filter((item) => !!item.upc && item.upc.trim().length > 0)
      .map((item) => ({
        value: item.upc as string,
        label: (
          <div className={styles.option}>
            <span className={styles.optionUpc}>{item.upc}</span>
            <span className={styles.optionSeparator}>|</span>
            <span className={styles.optionName}>{item.code_text}</span>
          </div>
        ),
        item,
      }))
  }, [data])

  const handleSearch = useCallback(
    (search: string) => {
      const now = Date.now()
      const delta = now - lastSearchTimeRef.current

      // Called right after a scan/paste — skip debounce, just sync the value
      if (isScanInputRef.current) {
        isScanInputRef.current = false
        lastSearchTimeRef.current = now
        onChange(search)
        return
      }

      // Strip the previous scan's value as a prefix — handles the case where a
      // second scan appends its chars to the first scan's result in the DOM
      // (common on Android before React flushes the cleared value)
      let effectiveSearch = search
      if (
        lastScannedValueRef.current &&
        search.length > lastScannedValueRef.current.length &&
        search.startsWith(lastScannedValueRef.current)
      ) {
        effectiveSearch = search.slice(lastScannedValueRef.current.length)
      }

      // Android fallback: track scanner speed via search-event timing
      if (lastSearchTimeRef.current !== 0 && delta < SCANNER_KEYSTROKE_THRESHOLD_MS) {
        rapidSearchCountRef.current++
        if (rapidSearchCountRef.current >= MIN_SCANNER_CHAR_COUNT) {
          setSuppressDropdown(true)
        }
      } else {
        rapidSearchCountRef.current = 1
        // Clear the field or a long enough pause → user is typing manually
        if (!effectiveSearch || (lastSearchTimeRef.current !== 0 && delta >= MANUAL_TYPING_RESET_MS)) {
          lastScannedValueRef.current = ''
          setSuppressDropdown(false)
        }
      }

      lastSearchTimeRef.current = now
      lastSearchValueRef.current = effectiveSearch

      setSearchValue(effectiveSearch)
      onChange(effectiveSearch)
    },
    [onChange],
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (!onScan) return
      const pasted = e.clipboardData.getData('text').trim()
      if (pasted) {
        isScanInputRef.current = true
        setSearchValue('')
        setSuppressDropdown(true)
        onScan(pasted)
      }
    },
    [onScan],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        scannerCharCountRef.current = 0
        scanBufferRef.current = ''
        lastKeyTimeRef.current = 0
        return
      }

      if (e.key === 'Enter') {
        // Accept scanner detection from either keydown timing (desktop) or
        // search-event timing (Android, where keydown gives 'Unidentified')
        const isScannerInput =
          scannerCharCountRef.current >= MIN_SCANNER_CHAR_COUNT ||
          rapidSearchCountRef.current >= MIN_SCANNER_CHAR_COUNT

        if (onScan && isScannerInput) {
          // Desktop: scanBufferRef has clean chars; Android: fall back to the
          // stripped value tracked in handleSearch
          const scannedValue = (scanBufferRef.current || lastSearchValueRef.current).trim()
          if (scannedValue) {
            e.preventDefault()
            isScanInputRef.current = true
            setSearchValue('')
            setSuppressDropdown(true)
            lastScannedValueRef.current = scannedValue
            rapidSearchCountRef.current = 0
            lastSearchTimeRef.current = 0
            onChange(scannedValue)
            onScan(scannedValue)
          }
        }

        scannerCharCountRef.current = 0
        scanBufferRef.current = ''
        lastKeyTimeRef.current = 0
        return
      }

      // Printable character — desktop path (e.key is reliable here)
      if (e.key.length === 1) {
        const now = Date.now()
        const delta = now - lastKeyTimeRef.current
        if (lastKeyTimeRef.current === 0 || delta >= SCANNER_KEYSTROKE_THRESHOLD_MS) {
          scannerCharCountRef.current = 1
          scanBufferRef.current = e.key
          if (lastKeyTimeRef.current !== 0) {
            setSuppressDropdown(false)
          }
        } else {
          scannerCharCountRef.current++
          scanBufferRef.current += e.key
        }
        lastKeyTimeRef.current = now
      }
    },
    [onScan, onChange],
  )

  const handleSelect = useCallback(
    (selectedValue: string, option: UpcOption) => {
      onChange(selectedValue)
      if (option?.item) {
        onSelectProduct(option.item)
      }
    },
    [onChange, onSelectProduct],
  )

  return (
    <div className={classNames(styles.wrapper, className)} onPaste={handlePaste} onKeyDown={handleKeyDown}>
      <Label label={label} required={required} />
      <AutoComplete<string, UpcOption>
        ref={inputRef}
        size={size}
        value={value}
        placeholder={placeholder}
        options={options}
        onSearch={handleSearch}
        onChange={onChange}
        onSelect={handleSelect}
        notFoundContent={isFetching ? <Spin size='small' /> : null}
        open={suppressDropdown ? false : undefined}
        disabled={disabled}
        className={styles.select}
        popupClassName={styles.dropdown}
        status={error ? 'error' : undefined}
        allowClear
        filterOption={false}
      />
      <ErrorLabel error={error} />
    </div>
  )
}
